import { Box, Button, Skeleton, Stack, Tab, Typography } from "@mui/material";
import { DataTable, FilterTabs, Flex, SearchField, UserAvatar } from "@zfp-akademie-a-s/zfp-design";
import { SearchIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { AnalysesAccessCell } from "Components/Analyses/Listing/AnalysesAccessCell";
import { AnalysesEmptyState } from "Components/Analyses/Listing/AnalysesEmptyState";
import { AnalysesFamilyMembersCell } from "Components/Analyses/Listing/AnalysesFamilyMembersCell";
import { AnalysesTableActionsCell } from "Components/Analyses/Listing/AnalysesTableActionsCell";
import { AnalysisStatusChip } from "Components/Analyses/_Shared/AnalysisStatusChip";
import { NewAnalysisButton } from "Components/_Shared/Buttons/NewAnalysisButton";
import { type DateRange, DateRangePicker } from "Components/_Shared/Inputs/DatePickers/DateRangePicker";
import { AuthenticatedLayout } from "Components/_Shared/Layouts/AuthenticatedLayout";
import { useListAnalysesOverview } from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  type AnalysesOverviewList,
  type AnalysisOverviewItem,
  AnalysisOverviewItemState,
  ListAnalysesOverviewScope,
  ListAnalysesOverviewSortBy,
  ListAnalysesOverviewSortOrder,
} from "Infrastructure/ApiZfp/Schema";
import { useFormatLocale } from "Infrastructure/Hooks/DateFns/useFormatLocale";
import useDebounce from "Infrastructure/Hooks/useDebounce";
import { FeatureFlag, useFeatureFlag } from "Infrastructure/Hooks/useFeatureFlag";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { format } from "date-fns";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const SHARED_FILTER = ListAnalysesOverviewScope.shared;

type AnalysesListingFilter = AnalysisOverviewItemState | typeof SHARED_FILTER | null;

export const AnalysesListingPage: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const { t } = useResource();
  const isAnalysisSharingEnabled = useFeatureFlag(FeatureFlag.AnalysisSharing);

  const [listingFilter, setListingFilter] = useState<AnalysesListingFilter>(null);
  const [searchValue, setSearchValue] = useState("");
  const [limit, setLimit] = useState(10);
  const [sortOrder, setSortOrder] = useState<ListAnalysesOverviewSortOrder>(ListAnalysesOverviewSortOrder.desc);
  const [sortBy, setSortBy] = useState<ListAnalysesOverviewSortBy | undefined>(ListAnalysesOverviewSortBy.updated_at);
  const [dateRange, setDateRange] = useState<DateRange | null>(null);
  const [page, setPage] = useState(0);
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const formatLocale = useFormatLocale();

  const { data, isLoading } = useListAnalysesOverview({
    state: listingFilter && listingFilter !== SHARED_FILTER ? listingFilter : undefined,
    scope: listingFilter === SHARED_FILTER ? SHARED_FILTER : undefined,
    dateFrom: dateRange?.startDate && format(dateRange.startDate, "yyyy-MM-dd"),
    dateTo: dateRange?.endDate && format(dateRange.endDate, "yyyy-MM-dd"),
    limit,
    page: page + 1,
    searchQuery: debouncedSearchValue,
    sortBy,
    sortOrder,
  });

  const handleFilterChange = (_: React.SyntheticEvent, filter: AnalysesListingFilter) => {
    setListingFilter(filter);
    setPage(0);
  };

  const handleClearSearch = () => {
    setListingFilter(null);
    setSearchValue("");
    setPage(0);
    setDateRange({
      startDate: undefined,
      endDate: undefined,
    });
  };

  const renderPageContent = (data: AnalysesOverviewList) => {
    if (data.totalRecordsUnfiltered === 0) {
      return (
        <AnalysesEmptyState
          title={t(Resources.AnalysesListing.Table.EmptyState.NoData.Title)}
          subtitle={t(Resources.AnalysesListing.Table.EmptyState.NoData.Message)}
          cta={
            <Box>
              <NewAnalysisButton />
            </Box>
          }
          icon={<SearchIcon sx={{ color: "text.secondary" }} />}
        />
      );
    }

    if (data.data.length === 0) {
      return (
        <AnalysesEmptyState
          title={t(Resources.AnalysesListing.Table.EmptyState.NoResult.Title)}
          subtitle={
            debouncedSearchValue
              ? t(Resources.AnalysesListing.Table.EmptyState.NoResultWithQuery.Message, {
                  value: debouncedSearchValue,
                })
              : t(Resources.AnalysesListing.Table.EmptyState.NoResult.Message)
          }
          cta={
            <Box display="flex">
              <Box mr={Spacing.L}>
                <Button variant="contained" color="secondary" onClick={handleClearSearch}>
                  {t(Resources.AnalysesListing.Table.EmptyState.ClearSearch)}
                </Button>
              </Box>
              <NewAnalysisButton />
            </Box>
          }
          icon={<SearchIcon sx={{ color: "text.secondary" }} />}
        />
      );
    }

    const agentsByGuid = new Map(data.references.agents.map((agent) => [agent.guid, agent]));

    return (
      <DataTable<AnalysisOverviewItem>
        rows={data.data}
        getRowId={(row) => row.versionId}
        onRowClick={(row) => {
          navigate(AppRouting.AnalysisDetail.path(row.analysisId, row.versionId));
        }}
        sort={sortBy ? { key: sortBy, direction: sortOrder } : null}
        onSortChange={(next) => {
          setSortBy(next.key as ListAnalysesOverviewSortBy);
          setSortOrder(next.direction as ListAnalysesOverviewSortOrder);
        }}
        pagination={{
          page,
          pageSize: limit,
          totalCount: data.pagination.totalRecords,
          onPageChange: setPage,
          onPageSizeChange: (size) => {
            setPage(0);
            setLimit(size);
          },
          pageSizeOptions: [5, 10, 25],
          labels: {
            rowsPerPage: t(Resources.Table.RowsPerPage),
            displayedRows: ({ page }) => `${page + 1}`,
            previousPage: t(Resources.Table.PrevPage),
            nextPage: t(Resources.Table.NextPage),
          },
        }}
        columns={[
          {
            id: "firstName",
            header: t(Resources.AnalysesListing.Table.HeaderCell.Client),
            renderCell: (row) => (
              <Box display="flex" alignItems="center">
                <UserAvatar firstName={row.firstName} lastName={row.surname} />
                <Box ml={Spacing.L}>
                  <Typography variant="body2" fontWeight={500}>
                    {row.firstName} {row.surname}
                  </Typography>
                  <Typography variant="body2" color="secondary">
                    {row.email}
                  </Typography>
                </Box>
              </Box>
            ),
          },
          {
            id: "clientRelations",
            header: t(Resources.AnalysesListing.Table.HeaderCell.Family),
            width: 120,
            renderCell: (row) => <AnalysesFamilyMembersCell clientRelations={row.clientRelations} />,
          },
          {
            id: "name",
            header: t(Resources.AnalysesListing.Table.HeaderCell.Name),
            sortKey: ListAnalysesOverviewSortBy.name,
            renderCell: (row) => (
              <Box>
                <Typography variant="body2">{row.name}</Typography>
              </Box>
            ),
          },
          {
            id: "state",
            header: t(Resources.AnalysesListing.Table.HeaderCell.State),
            sortKey: ListAnalysesOverviewSortBy.state,
            renderCell: (row) => (
              <Box>
                <AnalysisStatusChip variant="filled" state={row.state} />
              </Box>
            ),
          },
          {
            id: "createdAt",
            header: t(Resources.AnalysesListing.Table.HeaderCell.DateCreated),
            width: 120,
            sortKey: ListAnalysesOverviewSortBy.created_at,
            renderCell: (row) => (
              <Box>
                <Typography variant="body2">{formatLocale(new Date(row.createdAt))}</Typography>
              </Box>
            ),
          },
          {
            id: "updatedAt",
            header: t(Resources.AnalysesListing.Table.HeaderCell.DateUpdated),
            width: 140,
            sortKey: ListAnalysesOverviewSortBy.updated_at,
            renderCell: (row) => (
              <Box>
                <Typography variant="body2"> {formatLocale(new Date(row.updatedAt))}</Typography>
              </Box>
            ),
          },
          isAnalysisSharingEnabled
            ? {
                id: "sharing",
                header: t(Resources.AnalysesListing.Table.HeaderCell.Access),
                renderCell: (row) => <AnalysesAccessCell sharing={row.sharing} agentsByGuid={agentsByGuid} />,
              }
            : {
                id: "action",
                header: "",
                width: 100,
                disableRowClick: true,
                renderCell: (row) => <AnalysesTableActionsCell analysisId={row.analysisId} versionId={row.versionId} state={row.state} />,
              },
        ]}
      />
    );
  };

  return (
    <AuthenticatedLayout
      title={t(Resources.AnalysesListing.Title)}
      header={{ title: t(Resources.AnalysesListing.Title), subtitle: t(Resources.AnalysesListing.Subtitle) }}
    >
      <Stack spacing={Spacing.XXXL}>
        <Box display="flex" justifyContent="space-between">
          <Flex gap={Spacing.L}>
            <FilterTabs value={listingFilter} onChange={handleFilterChange}>
              <Tab label={t(Resources.AnalysesListing.Status.All)} value={null} />
              <Tab label={t(Resources.AnalysesListing.Status.InProgress)} value={AnalysisOverviewItemState.in_progress} />
              <Tab label={t(Resources.AnalysesListing.Status.Completed)} value={AnalysisOverviewItemState.completed} />
              <Tab label={t(Resources.AnalysesListing.Status.Declined)} value={AnalysisOverviewItemState.declined} />
              {isAnalysisSharingEnabled && <Tab label={t(Resources.AnalysesListing.Status.Shared)} value={SHARED_FILTER} />}
            </FilterTabs>
            <DateRangePicker
              title={t(Resources.Common.Created)}
              value={dateRange}
              onChange={(range) => {
                setDateRange(range);
              }}
            />
          </Flex>
          <SearchField
            placeholder={t(Resources.Common.Search)}
            ariaLabel={t(Resources.Common.Search)}
            clearLabel={t(Resources.Common.ClearSearch)}
            value={searchValue}
            sx={{ width: 320 }}
            onChange={setSearchValue}
          />
        </Box>

        {isLoading && <Skeleton variant="rounded" height={500} />}
        {data && renderPageContent(data)}
      </Stack>
    </AuthenticatedLayout>
  );
};
